import type { PageItem } from '@/db/content';

const NEWS_ARTICLE_TYPE = 'home.NewsArticlePage';
const NEWS_INDEX_TYPE = 'home.NewsIndexPage';
const PATH_SEGMENT_LEN = 4;

export function resolveLocaleNewsIndexPath(
  items: PageItem[],
  locale: string
): string {
  const indexItem = items.find(
    (item) => item.meta?.type === NEWS_INDEX_TYPE && item.meta?.locale === locale
  );
  return indexItem?.meta?.path ?? '';
}

export function getLocaleNewsArticles(
  items: PageItem[],
  locale: string,
  newsIndexPath = resolveLocaleNewsIndexPath(items, locale)
): PageItem[] {
  if (!items.length || !newsIndexPath) return [];
  return items.filter((item) => {
    if (item.meta?.type !== NEWS_ARTICLE_TYPE || item.meta?.locale !== locale) {
      return false;
    }
    const path = item.meta?.path ?? '';
    return (
      path.startsWith(newsIndexPath) &&
      path.length === newsIndexPath.length + PATH_SEGMENT_LEN
    );
  });
}

export function sortNewsArticles(articles: PageItem[]): PageItem[] {
  return [...articles].sort((a, b) => {
    const dateA = a.date ?? '';
    const dateB = b.date ?? '';
    if (dateA !== dateB) return dateB.localeCompare(dateA);
    return (b.id ?? 0) - (a.id ?? 0);
  });
}

export function getSortedNewsArticles(
  items: PageItem[],
  locale: string,
  newsIndexPath?: string
): PageItem[] {
  return sortNewsArticles(getLocaleNewsArticles(items, locale, newsIndexPath));
}

export function getNewsArticleById(
  items: PageItem[],
  articleId: number | null | undefined
): PageItem | null {
  if (typeof articleId !== 'number') return null;
  return (
    items.find(
      (item) => item.id === articleId && item.meta?.type === NEWS_ARTICLE_TYPE
    ) ?? null
  );
}

export function resolveNewsArticle(
  items: PageItem[],
  locale: string,
  articleId?: number | null,
  newsIndexPath?: string
): PageItem | null {
  if (typeof articleId === 'number') {
    return getNewsArticleById(items, articleId);
  }
  return getSortedNewsArticles(items, locale, newsIndexPath)[0] ?? null;
}
